import type { ChatReconRow, ReconciliationListArtifactData } from '@eriko/shared-types';
import { formatCurrency, formatDate } from '@eriko/shared-types';
import { Badge } from '@eriko/ui/src/components/ui/badge';
import { Button } from '@eriko/ui/src/components/ui/button';
import { Check, ChevronDown, ChevronRight, Link as LinkIcon, Paperclip } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useReconciliationQueue } from '../../hooks/useReconciliation';
import { AttachReceiptDialog, type AttachReceiptTarget } from './AttachReceiptDialog';
import { MatchToExistingDialog, type MatchTarget } from './MatchToExistingDialog';

interface Props {
  data: ReconciliationListArtifactData;
}

/**
 * Optimistic resolution state for a row the SME just acted on. The chat-card
 * artifact is a snapshot in time — without local state, the row stays in
 * `Needs your help` after the SME attaches a receipt or proposes a match,
 * which looks broken. Tracking resolution here lets the row collapse into a
 * `Matched ✓` confirmation strip immediately (Wonso parity).
 */
interface RowResolution {
  outcome: 'attached' | 'proposed';
  matchedSummary: string;
  matchedAmount: string | null;
  variance: number | null;
}

/**
 * SME inline reconciliation card embedded in chat.
 * Renders the grouped queue (auto-matched, suggested, needs-help, transfers)
 * as a view-only summary. Each "needs help" row exposes a Match-to-existing
 * dialog AND an Attach-receipt dialog. After a successful action, the row
 * collapses into a confirmation strip in place — no full refresh needed.
 */
export function ReconciliationListCard({ data }: Props) {
  const { summary, groups, period } = data;
  // Optimistic state populated immediately after a dialog reports success.
  // Persists for the life of this card render so the user sees their action
  // take effect even before the live query refetches.
  const [resolved, setResolved] = useState<Record<string, RowResolution>>({});
  const markResolved = (transactionId: string, r: RowResolution) =>
    setResolved((prev) => ({ ...prev, [transactionId]: r }));

  // Live queue: re-render the chat card from the latest DB state on every
  // mount, so the conversation history reflects every action the SME has
  // taken since the artifact was emitted. Scoped to the same monthly close
  // the artifact was emitted for — counts for THIS statement, not org-wide.
  const { data: liveQueue } = useReconciliationQueue(data.monthlyCloseId);

  // Set of transactionIds that are NO LONGER in needs-help according to
  // the live queue (because they were auto-matched / suggested / confirmed
  // after the artifact was emitted). These rows render as resolved.
  // Map keyed by transactionId carrying enough info (matched-object summary,
  // amount) to render the green confirmation strip with the real vendor name
  // after a page reload — not a generic "Resolved since this card was sent"
  // placeholder. The Map preserves vendor name + amount from the live queue
  // so the chat history reads correctly long after the artifact was emitted.
  const liveResolvedTxns = useMemo(() => {
    const m = new Map<
      string,
      { matchedSummary: string; matchedAmount: string | null; variance: number | null }
    >();
    if (!liveQueue?.groups) return m;
    for (const r of liveQueue.groups.autoMatched) {
      m.set(r.transactionId, {
        matchedSummary: r.matchedObjectSummary ?? 'Matched',
        matchedAmount: r.matchAmount ?? null,
        variance: r.variance ?? 0,
      });
    }
    for (const r of liveQueue.groups.suggestedMatches) {
      m.set(r.transactionId, {
        matchedSummary: r.matchedObjectSummary ?? 'Matched',
        matchedAmount: r.matchAmount ?? null,
        variance: r.variance ?? 0,
      });
    }
    return m;
  }, [liveQueue]);

  // Counts: prefer live counts when available so the SME and admin views
  // agree. Fall back to the artifact's frozen summary on first paint.
  // Use `needsHelpUnique` when present — one txn with 3 flags is still 1
  // item of work, but summing per-category counts would over-count it.
  const liveCounts = liveQueue?.counts;
  const liveNeedsHelpCount = liveCounts
    ? liveCounts.needsHelpUnique ??
      // Fallback when the API hasn't been redeployed: still better than
      // raw sum, but no de-dupe of multi-flag txns.
      (liveCounts.missingDocuments +
        liveCounts.unmatchedTransactions +
        liveCounts.ambiguousVendor +
        liveCounts.amountMismatch)
    : null;
  const liveMatchedCount = liveCounts ? liveCounts.autoMatched : null;
  const liveSuggestedCount = liveCounts ? liveCounts.suggestedMatches : null;

  // Only count optimistic resolutions that the live query hasn't picked up
  // yet. Once a confirmed match shows up in liveQueue.groups.autoMatched (or
  // .suggestedMatches), liveMatchedCount/liveNeedsHelpCount already reflect
  // it — adding the optimistic count on top would double-count.
  const pendingOptimisticCount = Object.keys(resolved).filter(
    (txnId) => !liveResolvedTxns.has(txnId),
  ).length;
  const matchedCount =
    (liveMatchedCount ?? summary.autoMatched.count) + pendingOptimisticCount;
  const suggestedCount = liveSuggestedCount ?? summary.suggested.count;
  const needsHelpRemaining = Math.max(
    0,
    (liveNeedsHelpCount ?? summary.needsHelp.count) - pendingOptimisticCount,
  );

  return (
    <div className="mt-3 rounded-lg border border-surface-200 bg-surface-0 overflow-hidden text-sm">
      <header className="px-4 py-3 border-b border-surface-200 bg-surface-50">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="font-semibold text-surface-900">
              Reconciliation{period ? ` — ${period}` : ''}
            </div>
            <div className="text-xs text-surface-500 mt-0.5">
              {summary.total} transactions
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <SummaryChip label="Eriko matched" value={matchedCount} tone="success" />
            <SummaryChip label="Eriko is reviewing" value={suggestedCount} tone="muted" />
            <SummaryChip label="Needs your help" value={needsHelpRemaining} tone="info" />
          </div>
        </div>
      </header>

      <Group
        title="Needs your help"
        rows={groups.needsHelp}
        defaultOpen={groups.needsHelp.length > 0}
        count={needsHelpRemaining}
      >
        {(row) => (
          <NeedsHelpRow
            key={row.transactionId}
            row={row}
            // Either the SME just resolved this in the current session
            // (optimistic) OR the live queue says it's no longer needing
            // help (persistent across reloads).
            resolution={
              resolved[row.transactionId] ??
              (() => {
                const live = liveResolvedTxns.get(row.transactionId);
                if (!live) return undefined;
                return {
                  outcome: 'attached',
                  matchedSummary: live.matchedSummary,
                  matchedAmount: live.matchedAmount,
                  variance: live.variance,
                };
              })()
            }
            onResolved={(r) => markResolved(row.transactionId, r)}
          />
        )}
      </Group>

      <Group title="Eriko is reviewing" rows={groups.suggested} defaultOpen={false}>
        {(row) => <ReadOnlyRow key={row.matchId ?? row.transactionId} row={row} />}
      </Group>

      <Group title="Eriko matched" rows={groups.autoMatched} defaultOpen={false}>
        {(row) => <ReadOnlyRow key={row.matchId ?? row.transactionId} row={row} />}
      </Group>

      <Group title="Transfers" rows={groups.transfers} defaultOpen={false}>
        {(row) => <ReadOnlyRow key={row.matchId ?? row.transactionId} row={row} />}
      </Group>
    </div>
  );
}

type Tone = 'success' | 'info' | 'muted' | 'warning';

const toneClasses: Record<Tone, string> = {
  success: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  info: 'bg-blue-50 text-blue-700 border-blue-200',
  warning: 'bg-amber-50 text-amber-700 border-amber-200',
  muted: 'bg-surface-100 text-surface-700 border-surface-200',
};

function SummaryChip({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: Tone;
}) {
  return (
    <span
      className={`text-[11px] font-medium px-2 py-1 rounded-md border ${toneClasses[tone]}`}
    >
      {label}: {value}
    </span>
  );
}

function Group<T extends ChatReconRow>({
  title,
  rows,
  defaultOpen,
  children,
  count,
}: {
  title: string;
  rows: T[];
  defaultOpen: boolean;
  children: (row: T) => React.ReactNode;
  /** Override the rendered count. Useful for "Needs your help" where a
   *  resolved-in-place row stays in `rows` for the green confirmation strip
   *  but shouldn't bump the header count above the chip's action count. */
  count?: number;
}) {
  const [open, setOpen] = useState(defaultOpen);
  if (rows.length === 0) return null;
  const headerCount = count ?? rows.length;
  return (
    <section className="border-b border-surface-200 last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-4 py-2 hover:bg-surface-50 text-left"
      >
        <span className="text-xs font-semibold text-surface-700 uppercase tracking-wide">
          {title} <span className="text-surface-400">({headerCount})</span>
        </span>
        {open ? (
          <ChevronDown className="w-3.5 h-3.5 text-surface-500" />
        ) : (
          <ChevronRight className="w-3.5 h-3.5 text-surface-500" />
        )}
      </button>
      {open && <ul className="divide-y divide-surface-100">{rows.map((r) => children(r))}</ul>}
    </section>
  );
}

function RowMeta({ row }: { row: ChatReconRow }) {
  return (
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm text-surface-900 truncate">{row.description}</span>
        {row.reasonBadge && (
          <Badge variant="outline" className="text-[11px] font-normal">
            {row.reasonBadge}
          </Badge>
        )}
      </div>
      <div className="text-[11px] text-surface-500 mt-0.5 font-mono">
        {formatDate(row.date)} ·{' '}
        <span className={row.type === 'CREDIT' ? 'text-emerald-700' : 'text-surface-700'}>
          {row.type === 'CREDIT' ? '+' : '-'}
          {formatCurrency(Number(row.amount), 2, row.currency)}
        </span>
        {row.matchedObjectSummary && (
          <>
            {' · '}
            <span className="text-surface-600">{row.matchedObjectSummary}</span>
          </>
        )}
      </div>
    </div>
  );
}

function NeedsHelpRow({
  row,
  resolution,
  onResolved,
}: {
  row: ChatReconRow;
  resolution: RowResolution | undefined;
  onResolved: (r: RowResolution) => void;
}) {
  const [attachOpen, setAttachOpen] = useState(false);
  const [matchOpen, setMatchOpen] = useState(false);

  const sharedTarget = {
    transactionId: row.transactionId,
    bankTransaction: {
      description: row.description,
      amount: row.amount,
      date: row.date,
      type: row.type,
    },
    currency: row.currency,
  };
  const attachTarget: AttachReceiptTarget = sharedTarget;
  const matchTarget: MatchTarget = sharedTarget;

  // Already-resolved row: compact single-line confirmation. Suppresses the
  // stale flag badge (e.g. "Eriko isn't sure who this vendor is") because the
  // SME just answered the question by attaching/matching. Inline "→ matched
  // with X" sits below the amount; variance + status badges sit at the right.
  if (resolution) {
    const matchedAmount = resolution.matchedAmount
      ? formatCurrency(parseFloat(resolution.matchedAmount), 2, row.currency)
      : null;
    const isZeroVariance =
      resolution.variance != null && Math.abs(resolution.variance) < 0.01;
    return (
      <li
        data-resolved=""
        className={[
          'px-4 py-2.5 bg-emerald-50',
          '!border-x !border-emerald-500',
          // top edge of a run (default) — emerald
          '!border-t !border-t-emerald-500',
          // …unless the previous sibling is also resolved → softer inner divider
          '[[data-resolved]+&]:!border-t-emerald-200',
          // bottom edge of a run (default) — emerald
          '!border-b !border-b-emerald-500',
          // …unless the next sibling is also resolved → no bottom (next row draws its own top)
          '[&:has(+[data-resolved])]:!border-b-0',
        ].join(' ')}
      >
        <div className="flex items-start gap-2.5">
          <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0 space-y-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-sm text-surface-900 truncate">{row.description}</span>
              <Badge
                variant="outline"
                className={`text-[11px] font-normal ${toneClasses.success}`}
              >
                {resolution.outcome === 'attached' ? 'Receipt attached' : 'Match proposed'}
              </Badge>
              {isZeroVariance && (
                <Badge
                  variant="outline"
                  className={`text-[11px] font-normal ${toneClasses.success}`}
                >
                  Matches bank line
                </Badge>
              )}
              {resolution.variance != null && Math.abs(resolution.variance) >= 0.01 && (
                <Badge
                  variant="outline"
                  className={`text-[11px] font-normal ${toneClasses.warning}`}
                >
                  Variance {resolution.variance > 0 ? '+' : ''}
                  {formatCurrency(resolution.variance, 2, row.currency)}
                </Badge>
              )}
            </div>
            <div className="text-[11px] text-surface-500 font-mono">
              {formatDate(row.date)} ·{' '}
              <span className={row.type === 'CREDIT' ? 'text-emerald-700' : 'text-surface-700'}>
                {row.type === 'CREDIT' ? '+' : '-'}
                {formatCurrency(Number(row.amount), 2, row.currency)}
              </span>
            </div>
            <div className="text-[11px] text-surface-600">
              <span className="text-surface-500">↳ matched with</span>{' '}
              <span className="font-medium text-surface-800">{resolution.matchedSummary}</span>
              {matchedAmount && (
                <span className="text-surface-500 font-mono ml-1">({matchedAmount})</span>
              )}
            </div>
            {resolution.outcome === 'proposed' && (
              <div className="text-[11px] text-surface-500 italic">
                Awaiting reviewer approval — Eriko will confirm this on review.
              </div>
            )}
          </div>
        </div>
      </li>
    );
  }

  return (
    <li className="px-4 py-2.5 flex items-start gap-3">
      <RowMeta row={row} />
      <div className="flex items-center gap-1.5 shrink-0">
        <Button
          size="sm"
          variant="outline"
          className="h-7 gap-1 px-2 text-xs"
          onClick={() => setMatchOpen(true)}
          title="Match to an existing bill, invoice, or receipt in your books"
        >
          <LinkIcon className="w-3.5 h-3.5" />
          Match
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-7 gap-1 px-2 text-xs"
          onClick={() => setAttachOpen(true)}
          title="Upload or attach a receipt for this transaction"
        >
          <Paperclip className="w-3.5 h-3.5" />
          Attach receipt
        </Button>
      </div>
      {attachOpen && (
        <AttachReceiptDialog
          item={attachTarget}
          onClose={() => setAttachOpen(false)}
          onAttached={(payload) =>
            onResolved({
              outcome: 'attached',
              matchedSummary: payload.matchedSummary,
              matchedAmount: payload.matchedAmount,
              variance: payload.variance,
            })
          }
        />
      )}
      {matchOpen && (
        <MatchToExistingDialog
          target={matchTarget}
          onClose={() => setMatchOpen(false)}
          onProposed={(payload) =>
            onResolved({
              outcome: 'proposed',
              matchedSummary: payload.matchedSummary,
              matchedAmount: payload.matchedAmount,
              variance: payload.variance,
            })
          }
        />
      )}
    </li>
  );
}

function ReadOnlyRow({ row }: { row: ChatReconRow }) {
  // Matched rows can expand to show the source ↔ target side-by-side with
  // confidence. Pure unmatched rows (no matchedObjectSummary) can't expand.
  const canExpand = !!row.matchedObjectSummary;
  const [expanded, setExpanded] = useState(false);
  return (
    <li
      className={canExpand ? 'hover:bg-surface-50 transition-colors' : undefined}
    >
      <div
        className={`px-4 py-2.5 flex items-start gap-3 ${canExpand ? 'cursor-pointer' : ''}`}
        onClick={canExpand ? () => setExpanded((v) => !v) : undefined}
        role={canExpand ? 'button' : undefined}
        aria-expanded={canExpand ? expanded : undefined}
        tabIndex={canExpand ? 0 : undefined}
        onKeyDown={
          canExpand
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setExpanded((v) => !v);
                }
              }
            : undefined
        }
      >
        <RowMeta row={row} />
        {canExpand && (
          <ChevronDown
            className={`w-3.5 h-3.5 text-surface-500 mt-1 shrink-0 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
          />
        )}
      </div>
      {canExpand && expanded && (
        <div className="px-4 pb-2.5">
          <MatchDetail row={row} />
        </div>
      )}
    </li>
  );
}

/**
 * Expanded source ↔ target detail strip for a matched/suggested row.
 * Mirrors Wonso's MatchDetailCard pattern: bank line on the left, arrow,
 * matched entity on the right, variance footer.
 */
function MatchDetail({ row }: { row: ChatReconRow }) {
  return (
    <div className="mt-2 rounded-md border border-surface-200 bg-surface-50 overflow-hidden text-xs">
      <div className="p-3 flex items-center gap-3">
        <div className="flex-1 text-right min-w-0">
          <div className="text-[11px] text-surface-500">Bank</div>
          <div className="text-sm font-medium text-surface-900 truncate">{row.description}</div>
          <div className="font-mono text-surface-700">
            {row.type === 'CREDIT' ? '+' : '-'}
            {formatCurrency(Number(row.amount), 2, row.currency)}
          </div>
        </div>
        <div className="text-surface-400 text-xs">→</div>
        <div className="flex-1 text-left min-w-0">
          <div className="text-[11px] text-surface-500">Matched to</div>
          <div className="text-sm font-medium text-surface-900 truncate">
            {row.matchedObjectSummary}
          </div>
          {typeof row.confidence === 'number' && (
            <div className="text-[11px] text-surface-500">
              {Math.round(row.confidence * 100)}% confidence
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
